import { AlertCircle, ImageOff } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { HOME } from "../../constants/testIds";

function formatPrice(price) {
  if (!price) return null;
  const amount = Number(price.amount);
  if (Number.isNaN(amount)) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: price.currencyCode || "USD",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${price.currencyCode || ""}`.trim();
  }
}

function ProductCard({ product }) {
  const image = product.featuredImage || product.images?.[0];
  const price = product.priceRange?.minVariantPrice;
  const priceStr = formatPrice(price);

  return (
    <Card
      data-testid={HOME.productCard}
      className="group overflow-hidden border-border transition-shadow duration-200 hover:shadow-md"
    >
      <div className="relative aspect-square w-full overflow-hidden bg-muted">
        {image?.url ? (
          <img
            src={image.url}
            alt={image.altText || product.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <ImageOff className="h-6 w-6" />
          </div>
        )}
        {!product.availableForSale && (
          <span className="absolute left-3 top-3 rounded-full bg-background/90 px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground backdrop-blur">
            Sold out
          </span>
        )}
      </div>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          {product.productType ? (
            <span className="truncate">{product.productType}</span>
          ) : (
            <span>Product</span>
          )}
          {product.vendor && (
            <>
              <span aria-hidden>·</span>
              <span className="truncate">{product.vendor}</span>
            </>
          )}
        </div>
        <h3
          data-testid={HOME.productTitle}
          className="line-clamp-2 text-sm font-medium leading-snug"
          title={product.title}
        >
          {product.title}
        </h3>
        <div className="flex items-center justify-between pt-1">
          <span
            data-testid={HOME.productPrice}
            className="text-sm font-semibold"
          >
            {priceStr ?? "—"}
          </span>
          {product.availableForSale ? (
            <Badge variant="secondary" className="text-[10px]">
              In stock
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px]">
              Unavailable
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SkeletonCard() {
  return (
    <Card className="overflow-hidden border-border">
      <Skeleton className="aspect-square w-full rounded-none" />
      <CardContent className="space-y-2 p-4">
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-4 w-4/5" />
        <div className="flex items-center justify-between pt-1">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-14" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function ProductGrid({ isLoading, isError, data, error }) {
  if (isLoading) {
    return (
      <div
        data-testid={HOME.productsLoading}
        className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4"
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div
        data-testid={HOME.productsError}
        className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
      >
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <div className="font-medium">Could not load products.</div>
          <div className="mt-1 text-xs">
            {String(error?.message || "Unknown error")}
          </div>
        </div>
      </div>
    );
  }

  const products = data?.products || [];

  if (products.length === 0) {
    return (
      <div
        data-testid={HOME.productsEmpty}
        className="rounded-md border border-dashed border-border p-10 text-center text-sm text-muted-foreground"
      >
        No products returned by the store yet.
      </div>
    );
  }

  return (
    <div
      data-testid={HOME.productsGrid}
      className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4"
    >
      {products.map((p) => (
        <ProductCard key={p.id} product={p} />
      ))}
    </div>
  );
}
